import trimEnd from 'lodash/trimEnd';
import trimStart from 'lodash/trimStart';

import {
  NODE_CLASS_TO_TYPE_MAP,
  NODE_CONFIGS,
  NODE_DATA_BY_TYPE,
  NODE_SECTION_TITLES,
  NODE_TYPE_TO_SECTION_MAP,
  NODE_TYPES,
} from '../../constants';
import { AnyNode, NodeConfig, NodeData, NodeType } from '../../types';
import noteUtils from './noteUtils';

/**
 * DO NOT TOUCH!!!
 *
 * We were accidentally saving our default node titles to the server, which
 * meant that we could not change them for previously created workflows.
 *
 * This returns what the default title was at the time of the override, which is
 * used by our node parser to determine whether this title should be overridden.
 *
 * See the following conversation for more details:
 * https://iterable.slack.com/archives/CN6649E9L/p1643407505487339
 */
const getOldNodeTitle = (nodeType: NodeType): string => {
  if (nodeType === NODE_TYPES.START) return 'Start when';
  if (nodeType === NODE_TYPES.NOTE) return 'Note';

  const section = NODE_TYPE_TO_SECTION_MAP[nodeType as keyof typeof NODE_TYPE_TO_SECTION_MAP];

  return trimEnd(NODE_SECTION_TITLES[section], 's'); // make singular
};

export const getFixedNodeTitle = (title: string | undefined, nodeType: NodeType) => {
  const titleDefault = nodeType === NODE_TYPES.NOTE ? '' : undefined;
  return getOldNodeTitle(nodeType) === title ? titleDefault : title;
};

/**
 * Checks if a node is a specific type, either using the node type or comparing
 * `nodeClass`/`nodeSuperClass` from the default node data.
 */
export const doesNodeMatchType = (nodeType: string, { nodeData = {}, type }: Partial<AnyNode>) => {
  if (nodeType === type) return true;
  const defaultNodeData = NODE_DATA_BY_TYPE[nodeType] as NodeData;

  return nodeData && defaultNodeData
    ? ['nodeClass', 'nodeSuperClass'].every((n) => nodeData[n] === defaultNodeData[n])
    : false;
};

/**
 * Frontend ids need to be a string, and backend needs to be an integer.  This
 * parses string frontend id to a number backend id for saving or other backend
 * calls.
 *
 * @param nodeId The id of the node to parse
 * @returns The number id of the node
 */
export const parseNodeIdToInt = (nodeId = ''): number => {
  const parsedId = trimStart(nodeId, noteUtils.prefix);
  return parseInt(parsedId, 10);
};

/**
 * Gets the corresponding node type for any given node
 * Expects the full node to be passed in
 */
export const getNodeTypeFromNode = ({
  nodeData = {},
  nodeDataJson = {},
  type: nodeType,
  isNote = false,
}: Partial<AnyNode>): NodeType => {
  if (isNote) return noteUtils.type;

  return (
    nodeType ||
    NODE_CLASS_TO_TYPE_MAP[nodeData?.nodeClass || nodeDataJson?.nodeClass] ||
    NODE_CLASS_TO_TYPE_MAP[nodeData?.nodeSuperClass || nodeDataJson?.nodeSuperClass] ||
    null
  );
};

/**
 * Gets the default configuration for a node type
 * @param nodeType The type of node to get the config for
 * @returns The default configuration for the node type
 */
export const getBaseNodeConfig = (nodeType: NodeType): NodeConfig | void => NODE_CONFIGS[nodeType];

/**
 * Changes the id of to a string, which is needed for the flowchart
 */
export const fixType = (i: number | string) => `${i}`;
